import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import { cn } from '../cn.js';
import { IconSpinner } from '../icons.js';

/**
 * The button.
 *
 * Every value is a token: sizes come from `size.control.*` through `button.height.*`, colours from
 * the semantic set, and the focus ring from `color.focus.ring`. The ring is never removed, because
 * a keyboard user must always see where they are.
 */
export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center whitespace-nowrap',
    'font-medium select-none',
    'rounded-(--button-radius) gap-(--button-gap)',
    'transition-colors duration-(--motion-duration-fast) ease-(--motion-easing-standard)',
    'outline-none focus-visible:ring-2 focus-visible:ring-(--color-focus-ring) focus-visible:ring-offset-2 focus-visible:ring-offset-(--color-surface)',
    /*
     * **Pointer when it can be pressed, not-allowed when it cannot.**
     *
     * A `<button>`'s own default cursor is the arrow, which says nothing, so the pointer is
     * declared here rather than left to the browser.
     *
     * This used to be `disabled:pointer-events-none`, which was wrong twice over: a native
     * disabled button already ignores clicks, so it bought nothing — and by removing the element
     * from hit-testing it also removed the `not-allowed` cursor and any tooltip, so a refused
     * control could not say why it was refused. The design system asks for exactly that ("disabled
     * controls remain readable and explain why where possible").
     */
    'cursor-pointer disabled:cursor-not-allowed disabled:opacity-(--opacity-disabled)',
    'motion-reduce:transition-none',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-(--color-accent) text-(--color-accent-foreground) hover:bg-(--color-accent-hover)',
        secondary:
          'bg-(--color-surface) text-(--color-text-primary) border border-(--color-border-strong) hover:bg-(--color-surface-sunken)',
        ghost: 'bg-transparent text-(--color-text-primary) hover:bg-(--color-surface-sunken)',
        danger:
          'bg-(--color-danger) text-(--color-danger-foreground) hover:bg-(--color-danger-hover)',
        link: 'bg-transparent text-(--color-accent) underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-(--button-height-sm) px-(--button-padding-inline-sm) text-(length:--font-size-caption)',
        md: 'h-(--button-height-md) px-(--button-padding-inline-md) text-(length:--font-size-body)',
        lg: 'h-(--button-height-lg) px-(--button-padding-inline-lg) text-(length:--font-size-body-large)',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Shows a spinner and makes the button inert, keeping its label so the width does not jump. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading = false, disabled, children, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      // `aria-busy` tells a screen reader the control is working; `disabled` stops interaction.
      aria-busy={loading || undefined}
      disabled={disabled === true || loading}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    >
      {loading ? (
        <IconSpinner
          aria-hidden="true"
          className="size-(--size-icon-sm) animate-spin motion-reduce:animate-none"
        />
      ) : null}
      {children}
    </button>
  );
});
